"use client";

import ServiceTitle from "@/shared/app/ui/text/ServiceTitle";
import { motion, Variants } from "framer-motion";
import Image from "next/image";

const YoutubeThumbnail = ({ videoId }: { videoId: string }) => {
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  const handleClick = () => {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, "_blank");
  };

  return (
    <div
      className="group relative w-full max-w-[1300px] aspect-video cursor-pointer 
                 overflow-hidden rounded-4xl mx-auto 
                 shadow-2xl shadow-black/20 transition-shadow duration-300 ease-in-out"
      onClick={handleClick}
    >
      {/* 썸네일 */}
      <Image
        src={thumbnailUrl}
        alt="유튜브 썸네일"
        fill
        sizes="(max-width: 1300px) 100vw, 1300px"
        className="object-cover transition-transform duration-700 ease-in-out group-hover:scale-[1.03]"
      />

      {/*  회색 오버레이 */}
      <div className="absolute inset-0 bg-black/30 transition-opacity duration-500 ease-in-out group-hover:opacity-60" />

      {/* 재생 버튼 컨테이너 */}
      <div className="absolute inset-0 flex justify-center items-center">
        <div
          className="w-20 h-20 bg-black/80 rounded-2xl flex justify-center items-center 
                    transition-all duration-500 ease-in-out 
                    group-hover:scale-105 group-hover:bg-black/90"
        >
          <svg
            className="w-10 h-10 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default function WesloInfoSection() {
  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.25 },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };
  const circleData = [
    { type: "SPACE", text: "공간" },
    { type: "RAMP", text: "경사로" },
    { type: "HUMAN", text: "사람" },
  ];

  return (
    <div className="border-t border-[#ECECEC] mt-[-2px]">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2 pt-30 md:pt-60">
        {/* 서비스 타이틀 */}

        <ServiceTitle
          badgeText={"입간판 경사로"}
          title={"위슬로"}
          description={`필요할 때 쉽게 펼쳐지는, 스마트한 입간판 겸용 경사로`}
        />

        {/* 로고 서비스 설명 */}
        <motion.div
          className="flex justify-center items-center flex-col xl:flex-row mt-30 md:mt-50 mb-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* 로고 */}
          <motion.div
            variants={itemVariants}
            className="w-full xl:w-[40%] justify-center items-center flex mr-0 xl:mr-10"
          >
            <div className="relative w-30 h-8 md:w-60 md:h-20 xl:w-80 xl:h-28 flex overflow-hidden">
              <Image
                src="/assets/images/page/service/weslo/logo.svg"
                alt="Symbol Logo"
                fill
                className="object-contain"
              />
            </div>
          </motion.div>

          {/* 텍스트 */}
          <div className="text-center xl:text-left flex flex-col grow">
            <motion.p
              variants={itemVariants}
              className="mt-6 xl:mt-0 text-2xl md:text-3xl xl:text-5xl mb-4 text-black-200 tracking-tight"
              style={{ fontFamily: '"paperlogy-bold", sans-serif' }}
            >
              장벽을 낮추고 사람을 잇다
            </motion.p>
            <motion.p
              variants={itemVariants}
              className="w-70 md:w-auto text-base md:text-xl mt-2 text-lightGray-40  leading-6 md:leading-9 break-keep tracking-tighter"
              style={{ fontFamily: '"pre-Medium", sans-serif' }}
            >
              ‘위슬로(weslo)’는 공간의 장벽을 낮추고 사람들 간의 관계를 잇는
              브랜드로,
              <br className="hidden sm:block" />
              매장 홍보용 입간판으로 활용하다가 필요할 때 손쉽게 경사로로
              변신하는 스마트한 제품입니다.
            </motion.p>
          </div>
        </motion.div>
      </div>

      {/* 이미지 설명 */}
      <div className="relative w-full max-w-[1820px] mx-auto px-4 md:px-6 xl:px-2 py-10 md:py-16 xl:py-20 mb-8 md:mb-40 overflow-hidden">
        {/* 이미지 */}

        <div className="relative w-full aspect-[402/180] xl:aspect-[1835/387]">
          <Image
            src="/assets/images/page/service/weslo/background.svg"
            alt="background"
            fill
            className="object-cover md:object-contain"
          />

          {/* 가로선 (양쪽에서 중앙으로 퍼짐) */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute top-1/2 left-0 w-full border-t-2 border-[#EC6C50] z-0 origin-center"
          />

          {/* 원형 3개 + 텍스트 */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { staggerChildren: 0.5, delayChildren: 0.2 },
              },
            }}
            className="absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center space-x-4 sm:space-x-6 md:space-x-8 xl:space-x-10"
          >
            {circleData.map((item, idx) => (
              <motion.div
                key={idx}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className={`
                w-24 h-24 sm:w-32 sm:h-32 xl:w-48 xl:h-48
                rounded-full flex flex-col items-center justify-center shadow-md
                ${
                  idx === 1
                    ? "bg-[#EC6C50] text-white"
                    : "bg-[#2E2626] text-[#EC6C50]"
                }
              `}
              >
                <span className="text-sm sm:text-base md:text-lg xl:text-2xl font-semibold">
                  {item.type}
                </span>
                <span className="text-xs sm:text-sm md:text-base xl:text-lg font-normal">
                  {item.text}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
      {/* 비디오 */}
      <div className="relative w-full mx-auto px-4 md:px-6 xl:px-2 py-0 md:py-6 xl:py-10 mb-10 md:mb-40 ">
        <YoutubeThumbnail videoId="7R6kP_DetcY" />
      </div>
    </div>
  );
}
